import React, { useState, useEffect, useRef } from 'react';
import { View, Text, ScrollView, TouchableOpacity, RefreshControl, Modal, TextInput } from 'react-native';
import CacheDataExplorer from './CacheDataExplorer';
import LoadingSpinner from './LoadingSpinner';
import { styles } from './cacheMonitoringTabStyles';
import { uiVar } from './floodlight/uiStyleCss';

/**
 * Admin Cache Monitoring Tab
 * 
 * Features:
 * - Manual cache refresh trigger
 * - Real-time logs display
 * - Browse all cached fixtures
 * - Live countdown to next auto-refresh
 * - Cache health and statistics
 */
const CacheMonitoringTab = () => {
  const [cacheStats, setCacheStats] = useState(null);
  const [cacheLogs, setCacheLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [countdown, setCountdown] = useState(null);
  
  // Live monitor states
  const [showLiveMonitor, setShowLiveMonitor] = useState(false);
  const [liveLogStream, setLiveLogStream] = useState([]);
  const [refreshRunning, setRefreshRunning] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(null);
  const [liveFixtureCount, setLiveFixtureCount] = useState(0);
  const [recentFixtures, setRecentFixtures] = useState([]);
  const [showRefreshDropdown, setShowRefreshDropdown] = useState(false);
  
  // Ref for auto-scrolling terminal
  const scrollViewRef = useRef(null);
  
  const functionsUrl = __DEV__ 
    ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
    : 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';
  
  // Fetch cache stats from backend
  const fetchCacheStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${functionsUrl}/getCacheStats`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setCacheStats(data);
      } else {
        throw new Error(data.error || 'Failed to fetch cache stats');
      }
    } catch (err) {
      console.error('[CacheMonitoring] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  // Fetch cache logs
  const fetchCacheLogs = async () => {
    try {
      const response = await fetch(`${functionsUrl}/getCacheLogs`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) throw new Error('Failed to fetch logs');
      
      const data = await response.json();
      if (data.success) {
        setCacheLogs(data.logs || []);
      }
    } catch (err) {
      console.error('[CacheLogs] Error:', err);
    }
  };
  
  // Manually trigger cache refresh
  const triggerCacheRefresh = async () => {
    try {
      setTriggering(true);
      
      const response = await fetch(`${functionsUrl}/triggerCacheRefresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[TriggerRefresh] HTTP Error:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText || 'Failed to trigger refresh'}`);
      }
      
      const data = await response.json();
      console.log('[TriggerRefresh] Response:', data);
      
      if (data.success && data.started) {
        // Open live monitor modal
        setLiveLogStream([
          { timestamp: new Date(), message: '🚀 Cache refresh started...', type: 'info' },
          { timestamp: new Date(), message: '⏱️ Estimated duration: 3-5 minutes', type: 'info' }
        ]);
        setRefreshRunning(true);
        setShowLiveMonitor(true);
        
        // Start polling for logs
        startLogPolling();
      } else if (data.running) {
        // Already running - show existing run in monitor
        alert(`⚠️ ${data.message}\n\nOpening monitor to show current progress...`);
        setRefreshRunning(true);
        setShowLiveMonitor(true);
        startLogPolling();
      } else {
        throw new Error(data.error || data.message || 'Unknown error');
      }
    } catch (err) {
      console.error('[TriggerRefresh] Error:', err);
      alert(`❌ Error: ${err.message}`);
    } finally {
      setTriggering(false);
    }
  };
  
  // Trigger SGP cache refresh only
  const triggerSGPRefresh = async () => {
    try {
      setTriggering(true);
      setShowRefreshDropdown(false);
      
      const response = await fetch(`${functionsUrl}/refreshAllSGPs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          limit: 10,
          options: {
            legCounts: [2, 3],
            topNPerLeg: 25,
            sportsbooks: ['DraftKings', 'BetMGM', 'Caesars'],
            minEV: 0
          }
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to trigger SGP refresh`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setLiveLogStream([
          { timestamp: new Date(), message: '🎯 SGP cache refresh started...', type: 'info' },
          { timestamp: new Date(), message: '📊 Generating SGPs for up to 10 fixtures...', type: 'info' },
          { timestamp: new Date(), message: '⏱️ Estimated duration: 5-10 minutes', type: 'info' }
        ]);
        setRefreshRunning(true);
        setShowLiveMonitor(true);
        alert('SGP refresh started! Monitor progress in the live terminal.');
      } else {
        throw new Error(data.error || 'Failed to start SGP refresh');
      }
    } catch (err) {
      console.error('[TriggerSGPRefresh] Error:', err);
      alert(`❌ Error: ${err.message}`);
    } finally {
      setTriggering(false);
    }
  };
  
  // Trigger players cache refresh
  const triggerPlayersRefresh = async () => {
    try {
      setTriggering(true);
      setShowRefreshDropdown(false);
      
      setLiveLogStream([
        { timestamp: new Date(), message: '👥 Starting Players Cache refresh...', type: 'info' },
        { timestamp: new Date(), message: '📊 Fetching players from Optic Odds API (NBA, NFL, NHL, MLB, NCAAB, NCAAF)...', type: 'info' },
        { timestamp: new Date(), message: '⏱️ Estimated duration: 1-3 minutes', type: 'info' }
      ]);
      setRefreshRunning(true);
      setShowLiveMonitor(true);
      
      const response = await fetch(`${functionsUrl}/refreshPlayersCache`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to trigger players refresh`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Extract stats from response (backend uses total_players and stats object)
        const totalPlayers = data.total_players || data.totalPlayers || 0;
        const stats = data.stats || {};
        const nbaCount = stats.basketball_nba?.players || 0;
        const nflCount = stats.football_nfl?.players || 0;
        const nhlCount = stats.hockey_nhl?.players || 0;
        const durationSec = data.duration_ms ? (data.duration_ms / 1000).toFixed(1) : 'N/A';
        
        setLiveLogStream(prev => [...prev, 
          { timestamp: new Date(), message: `✅ Players cache refreshed!`, type: 'success' },
          { timestamp: new Date(), message: `📊 Total players fetched: ${totalPlayers}`, type: 'info' },
          { timestamp: new Date(), message: `🏀 NBA: ${nbaCount} | 🏈 NFL: ${nflCount} | 🏒 NHL: ${nhlCount}`, type: 'info' },
          { timestamp: new Date(), message: `⏱️  Duration: ${durationSec}s`, type: 'info' }
        ]);
        
        // Refresh main stats
        await fetchCacheStats();
      } else {
        throw new Error(data.error || 'Failed to refresh players cache');
      }
    } catch (err) {
      console.error('[TriggerPlayersRefresh] Error:', err);
      setLiveLogStream(prev => [...prev, 
        { timestamp: new Date(), message: `❌ Error: ${err.message}`, type: 'error' }
      ]);
      alert(`❌ Error: ${err.message}`);
    } finally {
      setTriggering(false);
      setRefreshRunning(false);
    }
  };
  
  // Trigger full refresh (odds + SGPs)
  const triggerFullRefresh = async () => {
    try {
      setTriggering(true);
      setShowRefreshDropdown(false);
      
      // Start with live monitor showing
      setLiveLogStream([
        { timestamp: new Date(), message: '🚀 Starting FULL refresh (Odds + SGPs)...', type: 'info' },
        { timestamp: new Date(), message: '📊 [1/2] Triggering odds cache refresh...', type: 'info' }
      ]);
      setRefreshRunning(true);
      setShowLiveMonitor(true);
      
      // First, trigger odds refresh
      const oddsResponse = await fetch(`${functionsUrl}/triggerCacheRefresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!oddsResponse.ok) {
        throw new Error('Failed to start odds refresh');
      }
      
      const oddsData = await oddsResponse.json();
      
      if (oddsData.success && oddsData.started) {
        setLiveLogStream(prev => [...prev, 
          { timestamp: new Date(), message: '✅ Odds refresh started!', type: 'info' },
          { timestamp: new Date(), message: '⏳ Waiting 5 seconds before starting SGPs...', type: 'info' }
        ]);
        
        // Wait 5 seconds
        await new Promise(resolve => setTimeout(resolve, 5000));
        
        // Then trigger SGP refresh
        setLiveLogStream(prev => [...prev,
          { timestamp: new Date(), message: '🎯 [2/2] Triggering SGP cache refresh...', type: 'info' }
        ]);
        
        const sgpResponse = await fetch(`${functionsUrl}/refreshAllSGPs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            limit: 10,
            options: {
              legCounts: [2, 3],
              topNPerLeg: 25,
              sportsbooks: ['DraftKings', 'BetMGM', 'Caesars'],
              minEV: 0
            }
          })
        });
        
        const sgpData = await sgpResponse.json();
        
        if (sgpData.success) {
          setLiveLogStream(prev => [...prev,
            { timestamp: new Date(), message: '✅ SGP refresh started!', type: 'info' },
            { timestamp: new Date(), message: '🎉 FULL refresh initiated! Both processes running in background.', type: 'info' },
            { timestamp: new Date(), message: '⏱️ Estimated total duration: 10-15 minutes', type: 'info' }
          ]);
          
          // Start polling for logs
          startLogPolling();
          
          // ⚠️ REMOVED: Alert is redundant since we show the live monitor
          // alert('Full refresh started! Both odds and SGPs are being generated. Monitor progress in the live terminal.');
        } else {
          setLiveLogStream(prev => [...prev,
            { timestamp: new Date(), message: `⚠️ SGP refresh failed: ${sgpData.error}`, type: 'error' }
          ]);
        }
      } else if (oddsData.running) {
        alert('Odds refresh is already running. Opening monitor...');
        startLogPolling();
      } else {
        throw new Error(oddsData.error || 'Failed to start odds refresh');
      }
    } catch (err) {
      console.error('[TriggerFullRefresh] Error:', err);
      setLiveLogStream(prev => [...prev,
        { timestamp: new Date(), message: `❌ Error: ${err.message}`, type: 'error' }
      ]);
      alert(`❌ Error: ${err.message}`);
    } finally {
      setTriggering(false);
    }
  };
  
  // Poll for new logs while refresh is running
  const startLogPolling = () => {
    const startTime = Date.now();
    let pollCount = 0;
    
    const pollInterval = setInterval(async () => {
      pollCount++;
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      
      console.log(`[LiveMonitor] Poll #${pollCount} at ${elapsed}s`);
      
      try {
        // Fetch latest logs
        const logsResponse = await fetch(`${functionsUrl}/getCacheLogs?limit=50`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (logsResponse.ok) {
          const logsData = await logsResponse.json();
          if (logsData.success && logsData.logs) {
            // Filter logs from last 10 minutes only (to hide old errors)
            const tenMinutesAgo = Date.now() - (10 * 60 * 1000);
            const recentLogs = logsData.logs.filter(log => {
              const logTime = log.timestamp?.toDate?.() || new Date(log.timestamp?._seconds * 1000 || 0);
              return logTime.getTime() > tenMinutesAgo;
            });
            
            // Convert to live log format with rich fixture details
            const formattedLogs = recentLogs.map(log => {
              let message = log.message;
              
              // ⭐ Enhanced display for phase logs (NEW!)
              if (log.type === 'phase_start') {
                message = `\n🔵 ${log.message}\n`;
              } else if (log.type === 'phase_complete') {
                message = `\n✅ ${log.message}\n`;
              }
              // ⭐ Enhanced display for fixture_processed logs
              else if (log.type === 'fixture_processed' && log.fixture) {
                const f = log.fixture;
                message = `🏀 ${f.away_team} @ ${f.home_team}\n`;
                if (log.stats) {
                  message += `   📊 ${log.stats.markets} markets, ${log.stats.odds?.toLocaleString() || '0'} odds\n`;
                }
                // Show sample odds
                if (log.sampleOdds && log.sampleOdds.length > 0) {
                  message += `   💰 Sample odds:\n`;
                  log.sampleOdds.slice(0, 2).forEach(sample => {
                    const oddsStr = sample.odds.map(o => 
                      `${o.selection || o.name}: ${o.price > 0 ? '+' : ''}${o.price}`
                    ).join(', ');
                    message += `      • ${sample.market}: ${oddsStr}\n`;
                  });
                }
              } else if (!message) {
                message = log.type === 'error' ? '❌ Error' : '✅ Success';
              }
              
              return {
                timestamp: log.timestamp,
                message,
                type: log.type,
                stats: log.stats,
                error: log.error,
                fixture: log.fixture,
                sampleOdds: log.sampleOdds
              };
            });
            
            // Always add a live status message if still running
            if (pollCount <= 100) { // Poll for up to 5 minutes (100 polls * 3s)
              formattedLogs.push({
                timestamp: new Date(),
                message: `⏳ Refresh in progress... (${elapsed}s elapsed)`,
                type: 'info'
              });
            }
            
            setLiveLogStream(formattedLogs);
            console.log(`[LiveMonitor] Loaded ${formattedLogs.length} log entries`);
          }
        }
        
        // Check refresh status
        const statsResponse = await fetch(`${functionsUrl}/getCacheStats`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setRefreshProgress(statsData);
          
          // Update live fixture count
          const fixtureCount = statsData.totals?.fixtures || 0;
          setLiveFixtureCount(fixtureCount);
          console.log(`[LiveMonitor] Fixture count: ${fixtureCount}, Running: ${statsData.refresh_running}`);
          
          // Only stop polling if:
          // 1. refresh_running is false AND
          // 2. We've polled at least 10 times (30 seconds) AND
          // 3. We have some fixtures cached
          if (!statsData.refresh_running && pollCount >= 10 && fixtureCount > 0) {
            console.log('[LiveMonitor] Stopping poll - refresh complete');
            setRefreshRunning(false);
            clearInterval(pollInterval);
            
            // ⭐ Only show completion banner if odds were actually cached
            const oddsStored = statsData.last_refresh?.stats?.oddsStored || 0;
            const fixturesWithOdds = statsData.last_refresh?.stats?.fixturesWithOdds || 0;
            
            if (oddsStored > 0) {
              // Success: Odds were cached
              setLiveLogStream(prev => [...prev, {
                timestamp: new Date(),
                message: '\n' +
                         '🎉 ========== REFRESH COMPLETE ==========\n\n' +
                         '📊 Final Stats:\n' +
                         `   🏢 Fixtures: ${fixtureCount}\n` +
                         `   ✅ With Odds: ${fixturesWithOdds}\n` +
                         `   ⏭️  Without Odds: ${statsData.last_refresh?.stats?.fixturesWithoutOdds || '?'}\n` +
                         `   📊 Markets: ${statsData.last_refresh?.stats?.marketsProcessed || '?'}\n` +
                         `   💎 Odds: ${oddsStored.toLocaleString()}\n` +
                         `   ⏱️  Duration: ${statsData.last_refresh?.duration_seconds?.toFixed(1) || 'N/A'}s\n\n` +
                         '✅ All data cached and ready!',
                type: 'success'
              }]);
            } else {
              // Info: No odds available yet
              setLiveLogStream(prev => [...prev, {
                timestamp: new Date(),
                message: '\n' +
                         '⏳ ========== REFRESH COMPLETE ==========\n\n' +
                         '📊 Summary:\n' +
                         `   🏢 Fixtures Found: ${fixtureCount}\n` +
                         `   ⏱️  Duration: ${statsData.last_refresh?.duration_seconds?.toFixed(1) || 'N/A'}s\n\n` +
                         '💡 No odds posted yet. Check back closer to game time!',
                type: 'info'
              }]);
            }
            
            // Refresh main stats
            await fetchCacheStats();
            await fetchCacheLogs();
          } else if (pollCount >= 100) {
            // Safety: stop after 5 minutes
            console.log('[LiveMonitor] Stopping poll - timeout');
            clearInterval(pollInterval);
            setRefreshRunning(false);
            
            // Add timeout message
            setLiveLogStream(prev => [...prev, {
              timestamp: new Date(),
              message: '\n⏱️  Polling timeout reached (5 minutes). Refresh may still be running in background.',
              type: 'info'
            }]);
          }
        }
        
        // Fetch recent fixtures (last 10)
        const fixturesResponse = await fetch(`${functionsUrl}/getAllCachedFixtures?limit=10`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (fixturesResponse.ok) {
          const fixturesData = await fixturesResponse.json();
          if (fixturesData.success && fixturesData.fixtures) {
            setRecentFixtures(fixturesData.fixtures);
            console.log(`[LiveMonitor] Recent fixtures: ${fixturesData.fixtures.length}`);
          }
        }
      } catch (err) {
        console.error('[LogPolling] Error:', err);
      }
    }, 3000); // Poll every 3 seconds
    
    // Store interval ID for cleanup
    window.cacheRefreshPollInterval = pollInterval;
    console.log('[LiveMonitor] Polling started - will poll for up to 5 minutes');
  };
  
  // Stop polling when monitor closes
  const closeLiveMonitor = () => {
    console.log('[LiveMonitor] Closing monitor and stopping polling');
    setShowLiveMonitor(false);
    if (window.cacheRefreshPollInterval) {
      clearInterval(window.cacheRefreshPollInterval);
      window.cacheRefreshPollInterval = null;
    }
  };
  
  // Clear stuck refresh status (for debugging)
  const clearStuckStatus = async () => {
    try {
      const confirm = window.confirm('Clear stuck refresh status? This will allow new refreshes to start.');
      if (!confirm) return;
      
      // Import Firebase
      const { getFirestore, doc, setDoc } = await import('firebase/firestore');
      const db = getFirestore();
      
      await setDoc(doc(db, '_cache_metadata', 'refresh_status'), {
        running: false,
        cleared_at: new Date(),
        cleared_manually: true
      });
      
      alert('✅ Stuck status cleared! You can now trigger a new refresh.');
      await fetchCacheStats();
    } catch (err) {
      console.error('[ClearStatus] Error:', err);
      alert(`❌ Error: ${err.message}`);
    }
  };
  
  // Calculate countdown to next refresh (hourly at :00)
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const nextHour = new Date(now);
      nextHour.setHours(now.getHours() + 1, 0, 0, 0);
      const diffMs = nextHour - now;
      const minutes = Math.floor(diffMs / 60000);
      const seconds = Math.floor((diffMs % 60000) / 1000);
      setCountdown(`${minutes}m ${seconds}s`);
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Load on mount and refresh every 30 seconds
  useEffect(() => {
    fetchCacheStats();
    fetchCacheLogs();
    
    const interval = setInterval(() => {
      fetchCacheStats();
      fetchCacheLogs();
    }, 30000); // Auto-refresh every 30s
    
    return () => clearInterval(interval);
  }, []);
  
  // Pull to refresh
  const onRefresh = () => {
    setRefreshing(true);
    fetchCacheStats();
    fetchCacheLogs();
  };
  
  // Get health color
  const getHealthColor = (health) => {
    switch (health) {
      case 'healthy': return '#10B981';
      case 'stale': return '#F59E0B';
      case 'critical': return '#EF4444';
      case 'never_refreshed': return '#6B7280';
      default: return '#6B7280';
    }
  };
  
  // Get health text
  const getHealthText = (health) => {
    switch (health) {
      case 'healthy': return '✅ Healthy';
      case 'stale': return '⚠️ Stale';
      case 'critical': return '🔴 Critical';
      case 'never_refreshed': return '⚪ Never Refreshed';
      default: return '❓ Unknown';
    }
  };
  
  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp._seconds * 1000);
    return date.toLocaleString();
  };
  
  // Render loading state
  if (loading && !cacheStats) {
    return (
      <View style={styles.container}>
        <View style={styles.centerContent}>
          <LoadingSpinner size={32} color="#8B5CF6" />
          <Text style={styles.loadingText}>Loading cache stats...</Text>
        </View>
      </View>
    );
  }
  
  // Render error state
  if (error && !cacheStats) {
    return (
      <View style={styles.container}>
        <View style={styles.centerContent}>
          <Text style={styles.errorTitle}>❌ Error Loading Stats</Text>
          <Text style={styles.errorMessage}>{error}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={fetchCacheStats}>
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }
  
  return (
    <ScrollView 
      style={styles.container}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#8B5CF6" />
      }
    >
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>🔄 Cache Monitoring</Text>
        <Text style={styles.subtitle}>Live backend cache statistics</Text>
      </View>
      
      {/* Info Card - Dual-Endpoint Strategy */}
      <View style={styles.infoCard}>
        <Text style={styles.infoTitle}>📚 How Caching Works</Text>
        <Text style={styles.infoText}>
          The system uses a <Text style={styles.infoBold}>dual-endpoint strategy</Text> to provide comprehensive coverage:
        </Text>
        <View style={styles.infoList}>
          <Text style={styles.infoListItem}>
            <Text style={styles.infoEmoji}>✅</Text> <Text style={styles.infoBold}>Fixtures WITH Odds</Text> - Games with live betting data (via /fixtures/active)
          </Text>
          <Text style={styles.infoListItem}>
            <Text style={styles.infoEmoji}>⏳</Text> <Text style={styles.infoBold}>Upcoming Fixtures</Text> - Future games for schedule display (via /fixtures)
          </Text>
        </View>
        <Text style={styles.infoFooter}>
          This ensures users always see relevant data - live odds when available, upcoming schedules when not.
        </Text>
      </View>
      
      {/* Quick Actions */}
      <View style={styles.actionsCard}>
        {/* Split Button: Main action + Dropdown */}
        <View style={styles.splitButtonWrapper}>
          <View style={styles.splitButtonContainer}>
            <TouchableOpacity 
              style={[styles.splitButtonMain, triggering && styles.actionButtonDisabled]} 
              onPress={triggerFullRefresh}
              disabled={triggering}
            >
              {triggering ? (
                <LoadingSpinner size={16} color="#FFFFFF" />
              ) : (
                <Text style={styles.actionButtonText}>🔄 Refresh Cache</Text>
              )}
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={[styles.splitButtonDropdown, triggering && styles.actionButtonDisabled]}
              onPress={() => setShowRefreshDropdown(!showRefreshDropdown)}
              disabled={triggering}
            >
              <Text style={styles.dropdownArrow}>{showRefreshDropdown ? '▲' : '▼'}</Text>
            </TouchableOpacity>
          </View>
          
          {/* Dropdown Menu */}
          {showRefreshDropdown && (
            <View style={styles.dropdownMenu}>
              <TouchableOpacity 
                style={styles.dropdownItem}
                onPress={triggerCacheRefresh}
              >
                <Text style={styles.dropdownItemText}>📊 Refresh Odds Cache Only</Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={styles.dropdownItem}
                onPress={triggerSGPRefresh}
              >
                <Text style={styles.dropdownItemText}>🎯 Refresh SGP Cache Only</Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={styles.dropdownItem}
                onPress={triggerPlayersRefresh}
              >
                <Text style={styles.dropdownItemText}>👥 Refresh Players Cache</Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={[styles.dropdownItem, styles.dropdownItemHighlight]}
                onPress={triggerFullRefresh}
              >
                <Text style={[styles.dropdownItemText, styles.dropdownItemTextHighlight]}>🔄 Refresh All Caches</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
        
        {cacheStats?.refresh_running && (
          <TouchableOpacity 
            style={[styles.actionButton, { backgroundColor: '#EF4444' }]} 
            onPress={clearStuckStatus}
          >
            <Text style={styles.actionButtonText}>🔧 Clear Stuck Status</Text>
          </TouchableOpacity>
        )}
      </View>
      
      {/* Countdown Timer */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>⏱️ Next Auto-Refresh</Text>
        <View style={styles.countdownContainer}>
          <Text style={styles.countdownText}>{countdown || 'Calculating...'}</Text>
          <Text style={styles.countdownSubtext}>Runs every hour at :00</Text>
        </View>
      </View>
      
      {/* Cache Health Card */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Cache Health</Text>
        <View style={[styles.healthBadge, { backgroundColor: getHealthColor(cacheStats?.cache_health) + '20' }]}>
          <Text style={[styles.healthText, { color: getHealthColor(cacheStats?.cache_health) }]}>
            {getHealthText(cacheStats?.cache_health)}
          </Text>
        </View>
      </View>
      
      {/* Last Refresh Card */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Last Refresh</Text>
        <View style={styles.statRow}>
          <Text style={styles.statLabel}>Time:</Text>
          <Text style={styles.statValue}>{formatTimestamp(cacheStats?.last_refresh?.timestamp)}</Text>
        </View>
        <View style={styles.statRow}>
          <Text style={styles.statLabel}>Age:</Text>
          <Text style={styles.statValue}>
            {cacheStats?.last_refresh?.age_minutes !== null 
              ? `${cacheStats.last_refresh.age_minutes} minutes ago` 
              : 'Never'}
          </Text>
        </View>
        <View style={styles.statRow}>
          <Text style={styles.statLabel}>Duration:</Text>
          <Text style={styles.statValue}>
            {cacheStats?.last_refresh?.duration_seconds 
              ? `${cacheStats.last_refresh.duration_seconds.toFixed(2)}s` 
              : 'N/A'}
          </Text>
        </View>
      </View>
      
      {/* ⭐ NEW: Current Cache State Card - Shows ACTUAL totals */}
      {cacheStats?.current_state && (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>📦 Current Cache State</Text>
          <Text style={styles.cardSubtitle}>Actual data in cache right now</Text>
          
          <View style={styles.phaseContainer}>
            <View style={styles.phaseItem}>
              <Text style={styles.phaseLabel}>🏟️ Fixtures</Text>
              <Text style={styles.phaseValue}>{cacheStats.current_state.total_fixtures?.toLocaleString()}</Text>
              <View style={styles.phaseBreakdown}>
                <Text style={[styles.phaseSubValue, { color: '#10B981' }]}>
                  ✓ {cacheStats.current_state.fixtures_with_odds || 0} with odds
                </Text>
                <Text style={[styles.phaseSubValue, { color: '#F59E0B' }]}>
                  ⏳ {cacheStats.current_state.fixtures_without_odds || 0} upcoming
                </Text>
              </View>
            </View>
            
            <View style={styles.phaseItem}>
              <Text style={styles.phaseLabel}>📊 Markets</Text>
              <Text style={styles.phaseValue}>{cacheStats.current_state.total_markets?.toLocaleString()}</Text>
            </View>
            
            <View style={styles.phaseItem}>
              <Text style={styles.phaseLabel}>💎 Total Odds</Text>
              <Text style={[styles.phaseValue, { color: uiVar('adm-accent', '#8B5CF6') }]}>
                {cacheStats.current_state.total_odds?.toLocaleString()}
              </Text>
            </View>
          </View>
          
          {/* League breakdown */}
          {cacheStats.current_state.by_league && (
            <View style={{ marginTop: 10, flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              {Object.entries(cacheStats.current_state.by_league).map(([league, count]) => (
                <View key={league} style={{ 
                  backgroundColor: uiVar('adm-raised', '#374151'), 
                  paddingHorizontal: 10, 
                  paddingVertical: 4, 
                  borderRadius: 12 
                }}>
                  <Text style={{ color: uiVar('adm-ink2', '#D1D5DB'), fontSize: 12 }}>
                    {league.toUpperCase()}: {count}
                  </Text>
                </View>
              ))}
            </View>
          )}
        </View>
      )}
      
      {/* Refresh Stats Card - Historical data from last refresh run */}
      {cacheStats?.last_refresh?.stats && (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>🔄 Last Refresh Run</Text>
          <Text style={styles.cardSubtitle}>Stats from most recent refresh execution</Text>
          
          {/* Phase Breakdown */}
          <View style={styles.phaseContainer}>
            <View style={styles.phaseItem}>
              <Text style={styles.phaseLabel}>Processed</Text>
              <Text style={styles.phaseValue}>{cacheStats.last_refresh.stats.fixturesProcessed || cacheStats.last_refresh.stats.fixturesWithOdds || 0} fixtures</Text>
            </View>
            
            <View style={styles.phaseItem}>
              <Text style={styles.phaseLabel}>Markets</Text>
              <Text style={styles.phaseValue}>
                {cacheStats.last_refresh.stats.marketsProcessed || cacheStats.last_refresh.stats.marketsStored || 0}
              </Text>
            </View>
            
            <View style={styles.phaseItem}>
              <Text style={styles.phaseLabel}>Odds Written</Text>
              <Text style={styles.phaseValue}>
                {(cacheStats.last_refresh.stats.oddsStored || 0).toLocaleString()}
              </Text>
            </View>
            
            <View style={styles.phaseItem}>
              <Text style={styles.phaseLabel}>Duration</Text>
              <Text style={styles.phaseValue}>
                {cacheStats.last_refresh.duration_seconds 
                  ? `${cacheStats.last_refresh.duration_seconds.toFixed(1)}s` 
                  : 'N/A'}
              </Text>
            </View>
          </View>
        </View>
      )}
      
      {/* Cache Data Explorer - Browse all cached fixtures and odds */}
      <View style={styles.card}>
        <CacheDataExplorer functionsUrl={functionsUrl} />
      </View>
      
      {/* Live Refresh Monitor Modal */}
      <Modal
        visible={showLiveMonitor}
        animationType="slide"
        transparent={true}
        onRequestClose={closeLiveMonitor}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, styles.liveMonitorContent]}>
            <View style={styles.modalHeader}>
              <View style={styles.liveMonitorHeaderLeft}>
                <Text style={styles.modalTitle}>🔴 Live Cache Refresh Monitor</Text>
                {refreshRunning && (
                  <LoadingSpinner size={16} color="#10B981" style={{ marginLeft: 12 }} />
                )}
              </View>
              <TouchableOpacity onPress={closeLiveMonitor}>
                <Text style={styles.modalClose}>✕</Text>
              </TouchableOpacity>
            </View>
            
            {/* Progress Summary */}
            {refreshProgress && (
              <View style={styles.liveProgressSummary}>
                <Text style={styles.liveProgressText}>
                  {refreshRunning 
                    ? '⏳ Cache refresh in progress...' 
                    : (refreshProgress.totals?.odds_estimated > 0 || cacheStats?.last_refresh?.stats?.oddsStored > 0)
                      ? '✅ Refresh complete!' 
                      : '⏳ Fixtures cached, odds not posted yet'}
                </Text>
                
                {refreshProgress.totals && (
                  <View style={styles.liveProgressBreakdown}>
                    <Text style={styles.liveProgressStats}>
                      📊 {refreshProgress.totals.fixtures || 0} total fixtures
                    </Text>
                    {cacheStats?.last_refresh?.stats && (
                      <View style={styles.liveProgressSplit}>
                        <Text style={[styles.liveProgressSubStat, { color: '#10B981' }]}>
                          ✓ {cacheStats.last_refresh.stats.fixturesWithOdds || 0} with odds
                        </Text>
                        <Text style={[styles.liveProgressSubStat, { color: '#F59E0B' }]}>
                          ⏳ {cacheStats.last_refresh.stats.fixturesWithoutOdds || 0} upcoming
                        </Text>
                      </View>
                    )}
                    <Text style={styles.liveProgressStats}>
                      📈 {refreshProgress.totals.markets || 0} markets | 
                      🎯 {refreshProgress.totals.odds_estimated?.toLocaleString() || 0} odds
                    </Text>
                  </View>
                )}
                
                {/* Live Fixture Counter */}
                {refreshRunning && liveFixtureCount > 0 && (
                  <View style={styles.liveCounterContainer}>
                    <Text style={styles.liveCounterText}>
                      🔥 {liveFixtureCount} fixtures processed
                    </Text>
                  </View>
                )}
              </View>
            )}
            
            {/* Recent Fixtures List (Live) */}
            {refreshRunning && recentFixtures.length > 0 && (
              <View style={styles.liveFixturesPanel}>
                <Text style={styles.liveFixturesTitle}>📋 Recently Cached Fixtures (Updates every 3s)</Text>
                <ScrollView style={styles.liveFixturesList} nestedScrollEnabled={true}>
                  {recentFixtures.map((fixture, idx) => (
                    <View key={fixture.id || idx} style={[
                      styles.liveFixtureItem,
                      !fixture.has_odds && styles.liveFixtureItemNoOdds
                    ]}>
                      <View style={styles.liveFixtureRow}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                          <Text style={styles.liveFixtureLeague}>{fixture.league?.toUpperCase()}</Text>
                          {fixture.has_odds ? (
                            <Text style={styles.liveOddsStatus}>✓</Text>
                          ) : (
                            <Text style={styles.liveNoOddsStatus}>⏳</Text>
                          )}
                        </View>
                        <Text style={styles.liveFixtureTime}>
                          {new Date(fixture.last_updated?._seconds * 1000 || Date.now()).toLocaleTimeString()}
                        </Text>
                      </View>
                      <Text style={styles.liveFixtureMatchup}>
                        {fixture.home_team} vs {fixture.away_team}
                      </Text>
                      <Text style={styles.liveFixtureMarkets}>
                        {fixture.has_odds 
                          ? `📊 ${fixture.markets_count || 0} markets cached`
                          : '⏳ No odds available yet'
                        }
                      </Text>
                    </View>
                  ))}
                </ScrollView>
              </View>
            )}
            
            {/* Terminal-like Log Output */}
            <View style={styles.liveTerminal}>
              <View style={styles.terminalHeader}>
                <Text style={styles.terminalTitle}>CONSOLE OUTPUT</Text>
                <Text style={styles.terminalSubtitle}>Updates every 3 seconds</Text>
              </View>
              
              <ScrollView 
                style={styles.terminalBody}
                contentContainerStyle={styles.terminalBodyContent}
                ref={scrollViewRef}
                onContentSizeChange={() => scrollViewRef.current?.scrollToEnd({ animated: true })}
              >
                {liveLogStream.length === 0 ? (
                  <Text style={styles.terminalLine}>
                    <Text style={styles.terminalTimestamp}>{new Date().toLocaleTimeString()}</Text>
                    <Text style={styles.terminalMessage}> Waiting for logs...</Text>
                  </Text>
                ) : (
                  liveLogStream.map((log, idx) => (
                    <View key={idx} style={styles.terminalLogBlock}>
                      <Text style={styles.terminalLine}>
                        <Text style={styles.terminalTimestamp}>
                          {(() => {
                            try {
                              if (!log.timestamp) return '';
                              // Handle Firestore timestamp
                              if (log.timestamp.toDate) {
                                return log.timestamp.toDate().toLocaleTimeString();
                              }
                              // Handle _seconds format
                              if (log.timestamp._seconds) {
                                return new Date(log.timestamp._seconds * 1000).toLocaleTimeString();
                          }
                              // Handle regular Date or timestamp string
                              const date = new Date(log.timestamp);
                              return isNaN(date.getTime()) ? '' : date.toLocaleTimeString();
                            } catch (e) {
                              return '';
                            }
                          })()}
                        </Text>
                        <Text style={[
                          styles.terminalMessage,
                          log.type === 'error' && styles.terminalError,
                          log.type === 'success' && styles.terminalSuccess,
                          log.type === 'info' && styles.terminalInfo
                        ]}>
                          {' '}{log.message}
                        </Text>
                      </Text>
                      {log.stats && (
                        <Text style={styles.terminalStats}>
                          {'  '}├─ Fixtures: {log.stats.fixturesProcessed} | 
                          Markets: {log.stats.marketsProcessed} | 
                          Odds: {log.stats.oddsStored}
                        </Text>
                      )}
                      {log.error && (
                        <Text style={styles.terminalError}>
                          {'  '}└─ Error: {log.error}
                        </Text>
                      )}
                    </View>
                  ))
                )}
              </ScrollView>
            </View>
            
            {/* Action Buttons */}
            <View style={styles.liveMonitorActions}>
              {!refreshRunning && (
                <TouchableOpacity 
                  style={styles.liveActionButton}
                  onPress={closeLiveMonitor}
                >
                  <Text style={styles.liveActionButtonText}>✓ Close Monitor</Text>
                </TouchableOpacity>
              )}
              {refreshRunning && (
                <Text style={styles.liveMonitorNote}>
                  Monitor will auto-close when refresh completes, or click ✕ to close now
                </Text>
              )}
            </View>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
};


export default CacheMonitoringTab;
